import React, { useState } from 'react';
import {
  EmployeePickerProps,
  SelectEmployeeRequest,
  SelectEmployeeResponse,
} from '@dreamai/types';
import './employee-picker.scss';
import ReactMarkdown from 'react-markdown';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../../api';
import { useAgent } from '../../../../contexts/AgentContext';
import {
  generateColorVariants,
  generateMessageColors,
} from '../../sidebar/threadUtils';

interface EmployeePickerComponentProps extends EmployeePickerProps {
  threadId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
}

const EmployeePicker: React.FC<EmployeePickerComponentProps> = ({
  employees,
  threadId,
  updateThreadViewVisualDescription,
  hired = false,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { getAgentById } = useAgent();
  const [loadingEmployee, setLoadingEmployee] = useState<string | null>(null);
  const [hiredEmployee, setHiredEmployee] = useState<string | null>(null);
  const [employeeErrors, setEmployeeErrors] = useState<Record<string, string>>(
    {},
  );
  const [selectedEmployee, setSelectedEmployee] = useState<number>(0);

  const getFirstName = (fullName: string): string => {
    return fullName.split(' ')[0];
  };

  const handleShare = async (employeeName: string, agentType: string) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `I just hired ${getFirstName(employeeName)}!`,
          text: `Check out my new ${agentType} - ${getFirstName(employeeName)}`,
          url: window.location.origin,
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      try {
        await navigator.clipboard.writeText(window.location.href);
        // You could show a toast notification here
        console.log('Link copied to clipboard');
      } catch (error) {
        console.log('Failed to copy link:', error);
      }
    }
  };

  const handleHireEmployee = async (employeeName: string): Promise<void> => {
    if (!threadId) {
      console.error('No thread ID available');
      return;
    }

    setLoadingEmployee(employeeName);
    // Clear any previous error for this employee
    setEmployeeErrors((prev) => ({ ...prev, [employeeName]: '' }));

    try {
      const token = await getAccessTokenSilently();

      const requestPayload: SelectEmployeeRequest = {
        thread_id: threadId,
        employee_name: employeeName,
      };

      const response = await api.post<SelectEmployeeResponse>(
        '/employee/select-employee',
        requestPayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.success) {
        console.log('Employee selected successfully:', response.data.message);
        setHiredEmployee(employeeName);
      } else {
        console.error('Failed to select employee:', response.data.message);
        setEmployeeErrors((prev) => ({
          ...prev,
          [employeeName]: 'An error occurred, please try again',
        }));
      }
    } catch (error) {
      console.error('Error selecting employee:', error);

      // Check if it's a network error
      if (
        error instanceof Error &&
        (error.message.includes('Network Error') ||
          error.message.includes('ERR_NETWORK') ||
          error.message.includes('Failed to fetch') ||
          (error as any).code === 'NETWORK_ERROR')
      ) {
        setEmployeeErrors((prev) => ({
          ...prev,
          [employeeName]: 'Check your connection and try again',
        }));
      } else {
        setEmployeeErrors((prev) => ({
          ...prev,
          [employeeName]: 'An error occurred, please try again',
        }));
      }
    } finally {
      setLoadingEmployee(null);
    }
  };

  if (hired) {
    // When hired is true, there should only be one employee
    const employee = employees[0];
    const agent = employee.agentId ? getAgentById(employee.agentId) : null;
    const colorVariants = agent?.predominant_colour
      ? generateColorVariants(agent.predominant_colour)
      : {
          base: '#97D5FC',
          midDarker: '#4A90C2',
          darker: '#4A90C2',
          lighter: '#E3F2FD',
        };

    return (
      <div className='employee-hired-view'>
        <div className='inner-hired-bubble'>
          <div
            className='hired-bubble'
            style={{
              background: `radial-gradient(50% 50% at 50% 50%, ${colorVariants.midDarker} 0%, rgba(255, 255, 255, 0) 100%)`,
            }}
          ></div>
          <div className='bubble-blocker'></div>
          <div className='hired-banner'>
            <div
              className='hired-message'
              style={generateMessageColors(
                agent?.predominant_colour || '#97D5FC',
              )}
            >
              👋 &nbsp;You've hired your new {agent?.agent_type || 'agent'}
            </div>
            <div
              className='share'
              onClick={() =>
                handleShare(employee.name, agent?.agent_type || 'agent')
              }
            >
              Share
            </div>
          </div>
          <div className='hired-content'>
            <img src={employee.imageUrl} alt={employee.name} />
            <div className='hired-name'>{employee.name}</div>
            <div className='hired-description'>
              <a
                href={`/conversations?agent_id=${employee.agentId}&new=true`}
                className='go-to-chat-button'
              >
                Start chatting with {getFirstName(employee.name)}
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentEmployee = employees[selectedEmployee];

  return (
    <div className='employee-picker'>
      {/* Desktop Layout */}
      <div className='employee-option-wrapper'>
        {employees
          .filter(
            (employee) => !hiredEmployee || employee.name === hiredEmployee,
          )
          .map((employee, index) => (
            <div key={index} className='employee-option'>
              <div className='inner-wrapper'>
                <img src={employee.imageUrl} alt={employee.name} />
                <h5>{employee.name}</h5>
                {hired || hiredEmployee === employee.name ? (
                  employee.agentId ? (
                    <a
                      href={`/conversations?agent_id=${employee.agentId}&new=true`}
                      className='hired-chat-link'
                    >
                      Chat with {getFirstName(employee.name)}
                    </a>
                  ) : (
                    <div className='hired'>Hired</div>
                  )
                ) : (
                  <>
                    <button
                      className='employee-hire-button'
                      onClick={() => handleHireEmployee(employee.name)}
                      disabled={loadingEmployee !== null}
                    >
                      {loadingEmployee === employee.name ? (
                        <>
                          <span className='spinner'></span>
                          Hiring...
                        </>
                      ) : (
                        `Hire ${employee.name}`
                      )}
                    </button>
                    {employeeErrors[employee.name] && (
                      <div className='error-message'>
                        {employeeErrors[employee.name]}
                      </div>
                    )}
                  </>
                )}
              </div>
              {!hired && (
                <div className='employee-backstory'>
                  <ReactMarkdown>{employee.backstory}</ReactMarkdown>
                </div>
              )}
            </div>
          ))}
      </div>

      {/* Mobile Layout */}
      <div className='employee-mobile-selector'>
        <div className='employee-images-row'>
          {employees.map((employee, index) => (
            <div
              key={index}
              className={`employee-image-selector ${
                selectedEmployee === index ? 'selected' : ''
              }`}
              onClick={() => setSelectedEmployee(index)}
            >
              <img src={employee.imageUrl} alt={employee.name} />
            </div>
          ))}
        </div>

        <div className='selected-employee-info'>
          <h5>{currentEmployee.name}</h5>
          {hired || hiredEmployee === currentEmployee.name ? (
            currentEmployee.agentId ? (
              <a
                href={`/conversations?agent_id=${currentEmployee.agentId}&new=true`}
                className='hired-chat-link'
              >
                Chat with {getFirstName(currentEmployee.name)}
              </a>
            ) : (
              <div className='hired'>Hired</div>
            )
          ) : (
            <>
              <button
                className='employee-hire-button'
                onClick={() => handleHireEmployee(currentEmployee.name)}
                disabled={loadingEmployee !== null}
              >
                {loadingEmployee === currentEmployee.name ? (
                  <>
                    <span className='spinner'></span>
                    Hiring...
                  </>
                ) : (
                  `Hire ${currentEmployee.name}`
                )}
              </button>
              {employeeErrors[currentEmployee.name] && (
                <div className='error-message'>
                  {employeeErrors[currentEmployee.name]}
                </div>
              )}
            </>
          )}
          <div className='employee-backstory'>
            <ReactMarkdown>{currentEmployee.backstory}</ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeePicker;
